/**
 * Pure string-transform functions for chat export formatting.
 *
 * These functions have no Node/Electron dependencies — they operate only on
 * strings and shared types. They are extracted here so both the Electron
 * exportService and the web export adapter can reuse the same rendering logic.
 */

import { Citation, ExportFormat, ExportOptions, UniqueCitations } from '../types/models';
import { escapeHtmlText } from './marked/htmlExportRenderer';

// ─── Reasoning blocks ────────────────────────────────────────────────────────

/**
 * Render a GPT-oss reasoning block in the target export format.
 * Mimics the chat container's blockquote style with left border for PDF.
 */
export function renderReasoningBlock(analysis: string, format: ExportFormat): string {
    if (!analysis || !analysis.trim()) return '';

    const trimmed = analysis.trim();

    switch (format) {
        case 'pdf':
            // Styling controlled by .reasoning-block CSS in exportFormatters.ts
            return `<div class="reasoning-block"><div style="font-size: 0.75rem; font-weight: 500; color: #a3a3a3; margin: 0 0 0.25rem 0; text-transform: uppercase; letter-spacing: 0.05em;">REASONING</div>${trimmed}</div>`;

        case 'markdown': {
            const lines = trimmed.split('\n');
            const quoted = lines.map((line) => `> ${line}`).join('\n');
            return `> _REASONING_\n>\n${quoted}\n\n`;
        }

        case 'txt': {
            const indented = trimmed
                .split('\n')
                .map((line) => `  ${line}`)
                .join('\n');
            return `REASONING\n${indented}\n\n`;
        }

        default:
            return '';
    }
}

// ─── Citations ────────────────────────────────────────────────────────────────

/** Group citations by file, mirroring the UI chip grouping. */
function groupCitations(citations: Citation[]): UniqueCitations[] {
    if (!citations || citations.length === 0) return [];

    const map = new Map<string, UniqueCitations>();
    citations.forEach((c) => {
        const fileId = c.source?.file_id ?? 'unknown';
        const fileName = c.source?.file_name ?? 'Unknown File';
        const existing = map.get(fileId);
        if (existing) {
            (existing.citations ||= []).push(c);
        } else {
            map.set(fileId, { file_id: fileId, file_name: fileName, citations: [c] });
        }
    });

    return Array.from(map.values());
}

/**
 * Render grouped citations as simple chips in the target format.
 * Only renders for assistant messages (caller must check role).
 *
 * TODO: Add a detailed citations option that includes the content shown in CitationModal.
 */
export function renderCitationsBlock(citations: Citation[], format: ExportFormat): string {
    const groups = groupCitations(citations);
    if (groups.length === 0) return '';

    const names = groups.map((g) => g.file_name || 'Unknown File');
    const numbered = names.map((name, idx) => `${idx + 1} ${name}`);

    switch (format) {
        case 'pdf': {
            const chips = numbered
                .map((citation) => {
                    const [num, ...nameParts] = citation.split(' ');
                    const name = nameParts.join(' ');
                    return `<span class="citation-chip"><span class="index">${num}</span> ${escapeHtmlText(name)}</span>`;
                })
                .join('');
            return `<div class="citation-chips">${chips}</div>`;
        }

        case 'markdown':
        case 'txt': {
            const chipText = numbered.join(' ');
            return `\n\n${chipText}\n`;
        }

        default:
            return '';
    }
}

// ─── Images ───────────────────────────────────────────────────────────────────

/** Render placeholder text when images are excluded from the export. */
export function renderImagePlaceholder(count: number, format: ExportFormat): string {
    const text =
        count === 1
            ? '[1 image not included in export]'
            : `[${count} images not included in export]`;

    switch (format) {
        case 'pdf':
            return `<div class="message-image-placeholder" style="color: var(--color-muted); font-style: italic; margin: 8px 0; font-size: 14px;">${escapeHtmlText(text)}</div>`;
        case 'markdown':
            return `*${text}*\n\n`;
        case 'txt':
            return `${text}\n\n`;
        default:
            return '';
    }
}

// Grid layout matching MessageContainer.tsx for PDF
function renderImagesAsPdfHtml(images: string[], _role: string): string {
    const elements = images
        .map(
            (src, idx) => `
                <div class="message-image-container">
                    <img class="message-image" src="${src}" alt="Image ${idx + 1}" />
                </div>`,
        )
        .join('');

    return `
            <div class="message-images">
                <div class="message-images-grid">
                    ${elements}
                </div>
            </div>`;
}

function renderImagesAsMarkdown(images: string[]): string {
    const md = images.map((src, idx) => `![Image ${idx + 1}](${src})`).join('\n');
    return md ? `${md}\n\n` : '';
}

function renderImagesAsPlainText(images: string[]): string {
    const placeholders = images.map((_, idx) => `[Image ${idx + 1}]`).join(' ');
    return placeholders ? `${placeholders}\n\n` : '';
}

/**
 * Render a set of images for a message in the target format.
 * When `includeImages` is false but images exist, delegates to `renderImagePlaceholder`.
 */
 // TODO : embedImages option to inline images as base64 in MD/TXT exports (currently only supports PDF since it uses HTML img tags)
export function renderImagesForMessage(
    images: string[],
    format: ExportFormat,
    role: string,
    includeImages: boolean,
): string {
    if (!images || images.length === 0) return '';

    if (!includeImages) {
        return renderImagePlaceholder(images.length, format);
    }

    switch (format) {
        case 'pdf':
            return renderImagesAsPdfHtml(images, role);
        case 'markdown':
            return renderImagesAsMarkdown(images);
        case 'txt':
            return renderImagesAsPlainText(images);
        default:
            return '';
    }
}

// ─── Message block assembly ───────────────────────────────────────────────────

/**
 * Assemble a single message's full output block from its components.
 *
 * For PDF: returns an HTML div with role/image/content regions.
 * For text formats: strips HTML from the role header and concatenates plaintext.
 */
export function formatMessageBlock(
    role: string,
    roleHeader: string,
    imagesBlock: string,
    content: string,
    format: ExportFormat,
    _options: Partial<ExportOptions>,
): string {
    if (format === 'pdf') {
        return `
                <div class="message ${role === 'user' ? 'user' : 'assistant'}">
                    ${roleHeader}
                    ${imagesBlock}
                    <div class="message-content">${content}</div>
                </div>`;
    }

    // buildRoleHeader returns plain text for md/txt formats — no stripping needed.
    if (format === 'markdown') {
        const headerPart = roleHeader ? `${roleHeader}\n\n` : '';
        return `${headerPart}${imagesBlock}${content}\n`;
    }

    // txt
    const headerPart = roleHeader ? `${roleHeader}\n` : '';
    return `${headerPart}${imagesBlock}${content}\n`;
}

// ─── Document wrapping (MD/TXT only) ─────────────────────────────────────────

function formatHeader(
    text: string,
    format: ExportFormat,
    title?: string,
    showTitle?: boolean,
): string {
    switch (format) {
        case 'markdown':
            if (showTitle && title) {
                return `# ${title}\n> ${text}\n\n`;
            }
            return `# ${text}\n\n`;

        case 'txt':
            if (showTitle && title) {
                return `${title}\n${text}\n${'='.repeat(50)}\n\n`;
            }
            return `${text}\n${'='.repeat(50)}\n\n`;

        default:
            return '';
    }
}

function formatFooter(text: string, format: ExportFormat): string {
    switch (format) {
        case 'markdown':
            return `\n---\n${text}\n`;
        case 'txt':
            return `\n${'='.repeat(50)}\n${text}\n`;
        default:
            return '';
    }
}

/**
 * Wrap assembled message blocks with optional header/footer for MD or TXT.
 *
 * PDF uses `renderExportDocumentFromBlocks` from exportFormatters.ts — do not
 * call this function for PDF format.
 */
export function wrapDocument(
    blocks: string[],
    options: ExportOptions,
    format: ExportFormat,
): string {
    const showTitle = options.config?.showTitle ?? true;
    const headerText = options.headerText || 'Chat Export';

    const header = options.config?.includeHeader
        ? formatHeader(headerText, format, options.title, showTitle)
        : '';
    const footer = options.config?.includeFooter
        ? formatFooter(options.footerText || '', format)
        : '';

    let separator: string;
    if (format === 'markdown') {
        separator = '\n---\n';
    } else {
        separator = `\n${'-'.repeat(30)}\n`;
    }

    return `${header}${blocks.join(separator)}${footer}`;
}

// ─── PDF HTML document shell ──────────────────────────────────────────────────

/**
 * Wrap an array of rendered message HTML blocks in a complete, themed PDF HTML document.
 * (MD/TXT equivalent is `wrapDocument` above.)
 */
export function renderExportDocumentFromBlocks(
    messageBlocksHtml: string[],
    options?: Partial<ExportOptions>,
    katexCss?: string,
): string {
    const { theme, config, headerText, footerText, title } = options || {};
    const includeHeader = config?.includeHeader ?? true;
    const includeFooter = config?.includeFooter ?? false;
    const showTitle = config?.showTitle ?? true;
    const effectiveTheme = theme || 'light';
    const effectiveHeaderText = headerText || 'EdgeRunner : Chat Export';
    const effectiveFooterText = footerText || '';

    let headerContent: string;
    if (!includeHeader) {
        headerContent = '';
    } else if (showTitle && title) {
        headerContent = `<div class="header-title">${title}</div><div class="header-meta">${effectiveHeaderText}</div>`;
    } else {
        headerContent = `<div>${effectiveHeaderText}</div>`;
    }

    const baseHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${showTitle ? effectiveHeaderText : 'Chat Export'}</title>
  <style>
    @page { margin: 0; }
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html::before { content: ''; position: fixed; inset: 0; z-index: -1; background: var(--color-bg); }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; line-height: 1.6; margin: 0; padding: 12px; }
    .container { max-width: 1200px; margin: 0 auto; }
    .header { display: ${includeHeader ? 'flex' : 'none'}; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid var(--border); padding-bottom: 6px; font-weight: 600; }
    .header-title { font-size: 18px; font-weight: 700; }
    .header-meta { font-size: 14px; color: var(--color-muted); }
    .footer { display: ${includeFooter ? 'block' : 'none'}; margin-top: 12px; border-top: 1px solid var(--border); padding-top: 6px; font-size: 12px; }
    .message { margin-bottom: 12px; padding: 12px; border-radius: 6px; border: 1px solid var(--border); page-break-inside: auto; }
    .message.user { border-left: 4px solid var(--color-user); padding-top: 8px; }
    .message.assistant { border-left: 4px solid var(--color-assistant); }
    .message-role { font-weight: 600; font-size: 14px; margin-bottom: 6px; display: inline-flex; align-items: center; gap: 6px; }
    .message-content, .message-content p, .message-content li, .message-content td, .message-content th, .message-content div { white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }
    .message-content { display: flow-root; }
    code { padding: 2px 4px; border-radius: 3px; font-family: 'Roboto Mono', 'Monaco', 'Menlo', monospace; }
    pre { padding: 12px; border-radius: 6px; overflow-x: auto; white-space: pre-wrap; }
    a { text-decoration: none; }
    a:hover { text-decoration: underline; }
    img { max-width: 100%; height: auto; display: block; }
    .message-images { display: flex; flex-direction: column; margin: 12px 0; }
    .message-images-grid { display: flex; flex-wrap: wrap; gap: 16px; justify-content: flex-start; }
    .message-image-container { width: 100%; border-radius: 6px; overflow: hidden; border: 1px solid var(--border); }
    .message-image { width: 100%; height: auto; object-fit: contain; max-height: var(--pdf-image-max-height); }
    .table-wrapper { overflow: hidden; margin: var(--space-sm) 0; max-width: 100%; }
    table { width: 100%; border-collapse: collapse; border: 1px solid var(--table-border); border-radius: 6px; overflow: hidden; }
    thead { background: var(--table-header-bg); }
    tbody tr { border-bottom: 1px solid var(--table-border); transition: background-color 0.15s; }
    tbody tr:hover { background: var(--table-hover-bg); }
    th { text-align: left; padding: var(--space-xs); font-weight: 500; color: var(--table-header-text); border-right: 1px solid var(--table-border); white-space: nowrap; }
    th:last-child { border-right: 0; }
    td { padding: var(--space-xs); color: var(--table-cell-text); border-right: 1px solid var(--table-border); }
    td:last-child { border-right: 0; }
    td:first-child { white-space: nowrap; }
    .message-content > *:first-child { margin-top: 0 !important; }
    .message-content > *:last-child { margin-bottom: 0 !important; }
    .message-content p { margin: var(--space-xxs) 0 !important; }
    .message-content li { margin: 0 0 var(--space-xxs) 0 !important; }
    .message-content li:last-child { margin-bottom: 0; }
    .citation-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; white-space: normal !important; }
    .citation-chip { display: inline-flex; align-items: center; gap: 6px; background: var(--chip-bg); color: var(--chip-fg); border-radius: 9999px; padding: 2px 10px; font-size: 12px; font-weight: 500; white-space: normal !important; }
    .citation-chip .index { background: var(--chip-index-bg); color: var(--chip-index-fg); font-size: 12px; font-weight: 700; width: 16px; height: 16px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; text-align: center; }
    .reasoning-block { border-left: 3px solid #a3a3a3; padding: 0.5rem 0.75rem; margin: 0.5rem 0; background: rgba(163, 163, 163, 0.08); }
    .math-block { text-align: center; margin: 8px 0; }
    @media print { .message { page-break-inside: auto; break-inside: auto; } .message-content { display: block; } }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      ${headerContent}
    </div>
    <div class="chat-stream">
      ${messageBlocksHtml.join('\n')}
    </div>
    <div class="footer">${includeFooter ? effectiveFooterText : ''}</div>
  </div>
</body>
</html>`;

    const lightThemeStyles = `
    :root {
      --color-bg: #ffffff;
      --color-surface: #f8f9fa;
      --color-surface-alt: #e9ecef;
      --color-text: #27262B;
      --color-muted: #5B5962;
      --color-accent: #dc3545;
      --color-assistant: #dc3545;
      --color-user: #5B5962;
      --code-bg: #f8f9fa;
      --border: #EDEDED;
      --border-alt: #B6B6B6;
      --space-xxs: 4px; --space-xs: 8px; --space-sm: 12px;
      --pdf-image-max-height: 600px;
      --chip-bg: #e9ecef;
      --chip-fg: #27262B;
      --chip-index-bg: #d1d5db;
      --chip-index-fg: #202025;
      --table-border: #EDEDED;
      --table-header-bg: #f1f3f4;
      --table-header-text: #27262B;
      --table-cell-text: #5B5962;
      --table-hover-bg: #f8f9fa;
    }
    html { color: var(--color-text); }
    body { color: var(--color-text); background: var(--color-bg); }
    .header { color: var(--color-text); }
    .footer { color: var(--color-muted); }
    .message { background: var(--color-surface); }
    .message.user { background: var(--color-surface-alt); border-color: var(--border-alt); }
    .message-role { color: var(--color-muted); }
    code { background: var(--code-bg); }
    pre { background: var(--code-bg); }
    a { color: #2563eb; }
  `;

    const darkThemeStyles = `
    :root {
      --color-bg: #202025;
      --color-surface: #27262B;
      --color-surface-alt: #303033;
      --color-text: #EDEDED;
      --color-muted: #B6B6B6;
      --color-accent: #E13239;
      --color-assistant: #E13239;
      --color-user: #B6B6B6;
      --code-bg: #1A191E;
      --border: #303033;
      --border-alt: #5B5962;
      --space-xxs: 4px; --space-xs: 8px; --space-sm: 12px;
      --pdf-image-max-height: 600px;
      --chip-bg: rgba(63,63,70,0.65);
      --chip-fg: #ffffff;
      --chip-index-bg: #52525b;
      --chip-index-fg: #e4e4e7;
      --table-border: #525252;
      --table-header-bg: #404040;
      --table-header-text: #EDEDED;
      --table-cell-text: #B6B6B6;
      --table-hover-bg: #404040;
    }
    html { color: var(--color-text); }
    body { color: var(--color-text); background: var(--color-bg); }
    .header { color: var(--color-text); }
    .footer { color: var(--color-muted); }
    .message { background: var(--color-surface); }
    .message.user { background: var(--color-surface-alt); border-color: var(--border-alt); }
    .message-role { color: var(--color-muted); }
    code { background: var(--code-bg); }
    pre { background: var(--code-bg); }
    a { color: #60a5fa; }
  `;

    const themeStyles =
        effectiveTheme === 'light' ? lightThemeStyles : darkThemeStyles;
    const katexBlock = katexCss ? `\n${katexCss}` : '';
    return baseHtml.replace('<style>', `<style>\n${themeStyles}${katexBlock}`);
}
